"use client";

// src/components/select/select.tsx
import { ChevronDownMedium } from "@cat-home-experts/mortar-iconography-web/ChevronDownMedium";
import {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";
import { useOutsideInteraction } from "../../utils/use-outside-interaction.mjs";
import { selectStyles } from "./select.styles.mjs";
import { usePopper } from "./use-popper.mjs";
import { jsx, jsxs } from "react/jsx-runtime";
var Select = ({
  name,
  options,
  value,
  disabled = false,
  onChange,
  label,
  ariaLabel,
  placeholder,
  error,
  size = "md",
  className,
  startContent,
  ref
}) => {
  const selectRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isPlaceholder, setIsPlaceholder] = useState(!!placeholder);
  const { styles } = usePopper(selectRef, { open: isOpen });
  const selectedValueLabel = options.find(
    (option) => option.value === value
  )?.label;
  const uid = useId();
  const listboxId = `${uid}-listbox`;
  const errorId = `${uid}-error`;
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    } else {
      inputRef.current?.blur();
    }
  }, [isOpen]);
  useOutsideInteraction(
    [selectRef, listRef],
    () => {
      setIsOpen(false);
    },
    { active: isOpen }
  );
  const handleSelect = useCallback(
    (option) => {
      selectRef.current?.focus();
      setIsPlaceholder(false);
      const event = {
        target: { name, value: option.value }
      };
      onChange?.(event);
      setIsOpen(false);
    },
    [onChange, name]
  );
  useImperativeHandle(ref, () => selectRef.current);
  const toggleSelect = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };
  const keyHandlers = {
    ArrowDown: (event) => {
      event.preventDefault();
      if (isOpen) {
        setHighlightedIndex((highlightedIndex + 1) % options.length);
      } else {
        setIsOpen(true);
      }
    },
    ArrowUp: (event) => {
      event.preventDefault();
      if (isOpen) {
        if (highlightedIndex === -1) {
          setHighlightedIndex(options.length - 1);
        } else {
          setHighlightedIndex(
            (highlightedIndex - 1 + options.length) % options.length
          );
        }
      } else {
        setIsOpen(true);
      }
    },
    Enter: (event) => {
      if (isOpen) {
        event.preventDefault();
        handleSelect(options[highlightedIndex]);
        setIsOpen(false);
      }
    },
    " ": (event) => {
      if (isOpen) {
        event.preventDefault();
        handleSelect(options[highlightedIndex]);
        setIsOpen(false);
      }
    },
    Escape: (event) => {
      if (isOpen) {
        event.preventDefault();
        setIsOpen(false);
      }
    }
  };
  const handleKeyDown = (event) => {
    const handler = keyHandlers[event.key];
    if (handler) {
      handler(event);
    } else if (!isOpen && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      setIsOpen(true);
    }
    selectRef.current?.focus();
  };
  const style = selectStyles({
    isOpen,
    isInvalid: !!error,
    size,
    disabled,
    placeholder: isPlaceholder
  });
  return /* @__PURE__ */ jsxs("div", { className: style.container({ className: className?.container }), children: [
    label && /* @__PURE__ */ jsx(
      "label",
      {
        className: style.selectLabel({
          className: className?.select
        }),
        htmlFor: uid,
        id: uid,
        children: label
      }
    ),
    /* @__PURE__ */ jsxs(
      "button",
      {
        className: style.selectWrapper({
          className: className?.selectWrapper
        }),
        type: "button",
        "aria-labelledby": uid,
        "aria-haspopup": "listbox",
        "aria-expanded": isOpen,
        "aria-controls": listboxId,
        "aria-autocomplete": "list",
        "aria-label": ariaLabel,
        role: "combobox",
        onKeyDown: handleKeyDown,
        disabled,
        onClick: toggleSelect,
        ref: selectRef,
        children: [
          startContent && /* @__PURE__ */ jsx("span", { className: style.startContent(), children: startContent }),
          /* @__PURE__ */ jsxs("div", { className: style.selectInner(), children: [
            /* @__PURE__ */ jsx("div", { className: style.select({ className: className?.select }), children: selectedValueLabel ? selectedValueLabel : placeholder }),
            /* @__PURE__ */ jsx("span", { className: style.endContent(), children: /* @__PURE__ */ jsx(ChevronDownMedium, { role: "presentation", size: 15, color: "#62687A" }) })
          ] })
        ]
      }
    ),
    isOpen && createPortal(
      /* @__PURE__ */ jsxs(
        "div",
        {
          className: style.selectContainer({
            className: className?.selectContainer
          }),
          style: styles,
          onKeyDown: handleKeyDown,
          children: [
            /* @__PURE__ */ jsx(
              "input",
              {
                ref: inputRef,
                id: uid,
                className: "sr-only",
                "aria-controls": listboxId,
                onChange,
                value,
                name,
                placeholder,
                type: "text",
                "aria-label": label,
                "aria-errormessage": error && errorId,
                "aria-invalid": !!error,
                "aria-describedby": error ? errorId : void 0
              }
            ),
            /* @__PURE__ */ jsx(
              "ul",
              {
                id: listboxId,
                ref: listRef,
                className: style.selectList({
                  className: className?.selectContainer
                }),
                role: "listbox",
                tabIndex: 0,
                children: options.map((option, index) => {
                  const isHighlighted = highlightedIndex === index;
                  const itemStyle = style.selectItem({
                    highlighted: isHighlighted,
                    className: className?.selectItem
                  });
                  return /* @__PURE__ */ jsx(
                    "li",
                    {
                      id: `${listboxId}-${option.label}`,
                      className: itemStyle,
                      onClick: () => handleSelect(option),
                      role: "option",
                      "aria-selected": value === option.label,
                      children: option.label
                    },
                    option.value
                  );
                })
              }
            )
          ]
        }
      ),
      document.body
    ),
    error && /* @__PURE__ */ jsx("span", { id: errorId, role: "alert", className: style.error(), children: error })
  ] });
};
export {
  Select
};
